import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref, ref } from "vue";
import type { Discovery } from "@/shared/agent-story/discovery";
import type { ToolNote } from "@/shared/agent-story/story";
import { request } from "@/shared/http/client";

/**
 * Missions: a headless agent round with a stated goal and a hard budget.
 *
 * Listed apart from chat turns because a mission is BILLABLE and a chat turn is
 * incidental — whoever is checking what an agent cost is looking for exactly
 * these rows.
 */
export interface MissionTask {
	readonly id: string;
	readonly label: string;
	readonly worker?: number;
	readonly status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
}

export interface MissionRun {
	readonly id: string;
	readonly agentCode: string;
	readonly mode: string;
	readonly objective: string;
	readonly status:
		| "queued"
		| "running"
		| "waiting_human"
		| "succeeded"
		| "failed"
		| "cancelled"
		| "budget_exhausted";
	readonly summary: string | null;
	readonly error: string | null;
	readonly depth: number;
	readonly usage: { inputTokens: number; outputTokens: number };
	/** O que o modelo está a escrever agora (só enquanto `running`): a ferramenta e o total de caracteres. */
	readonly writing?: { tool: string; chars: number };
	/** `output` is absent while that call is still running; `isError` on old rows too. */
	readonly steps: ReadonlyArray<{
		tasks?: readonly MissionTask[];
		tool: string;
		input: string;
		output?: string;
		ms: number;
		isError?: boolean;
		/**
		 * O ACHADO do passo, quando a ferramenta soube dizê-lo em dados: quantos
		 * resultados, o título da página, o endereço, o logotipo. É o que a tela
		 * transforma em história — ver `shared/agent-story/story.ts`. Ausente em
		 * quase todo passo, e em TODO passo gravado antes de o servidor o emitir.
		 */
		note?: ToolNote;
		/**
		 * O que a ferramenta ENCONTROU, em dados (04/10) — as mesmas descobertas do
		 * quadro `found` do fio, gravadas no passo: um recarregamento a meio, ou a
		 * fase seguinte (outra missão), ainda tem o dossiê.
		 */
		found?: readonly Discovery[];
	}>;
	readonly startedAt: string | null;
	readonly endedAt: string | null;
	readonly createdAt: string;
	/**
	 * A CORRIDA DA FUNÇÃO que pediu esta missão — o elo para subir até à regra
	 * (`GET /v3/executions/:id`). `null` numa missão que uma pessoa mandou rodar
	 * à mão, e em toda missão gravada antes de o elo existir (19/09/2026).
	 */
	readonly artifactRunId: string | null;
}

/**
 * A mission as the LIST carries it: the row, without the steps.
 *
 * The steps used to travel with every row — trimmed to 300 characters per call,
 * which for fifty missions of a few dozen calls each is still a payload measured
 * in hundreds of kilobytes for a table that prints a title and a date. The count
 * is the only thing about them a row ever said, so the count is what comes;
 * whoever opens one gets the steps whole from `useMission`.
 */
export interface MissionSummary extends Omit<MissionRun, "steps"> {
	readonly stepCount: number;
}

/** Onde a lista está — nada mais: o log não tem filtro, tem ordem e página. */
export interface MissionQuery {
	readonly limit: number;
	readonly offset: number;
}

export const PAGE_SIZE = 25;
export const PAGE_SIZES: readonly number[] = [PAGE_SIZE, 50, 100];

/** A primeira página, que é a que o crachá do menu e a home compartilham. */
export function firstPage(): MissionQuery {
	return { limit: PAGE_SIZE, offset: 0 };
}

export const missionKeys = {
	list: (query: MissionQuery) => ["missions", "list", query] as const,
	one: (id: string) => ["missions", id] as const,
};

/** O envelope da lista: a página, e os dois números que são do log INTEIRO. */
export interface MissionListPage {
	readonly missions: readonly MissionSummary[];
	readonly total: number;
	/** Quantas ainda vão mexer sozinhas — contadas no servidor, sobre tudo. */
	readonly open: number;
}

/**
 * Statuses a run will not leave on its own. `waiting_human` will, once answered.
 *
 * What "ainda vai mexer" MEANS, written once. The count of it no longer happens
 * here — the server sends `open` with every page, because a badge in the menu of
 * every screen cannot depend on which page of the log somebody is looking at.
 * What is still read from this set is the LEASH: an open detail stops asking the
 * moment the run it is watching settles.
 */
export const SETTLED: ReadonlySet<MissionRun["status"]> = new Set([
	"succeeded",
	"failed",
	"cancelled",
	"budget_exhausted",
]);

// A cadência é do observador (27/08, zero polling): o relógio só existe
// enquanto alguma missão está EM VOO — uma operação que termina sozinha e leva
// o relógio junto. Lista assentada não pergunta nada; foco e mutações atualizam.
//
// Quem decide agora é `open`, contado pelo SERVIDOR sobre o log inteiro: olhar
// só as linhas da página faria o relógio depender de em que página alguém está.
/**
 * The log, by the page.
 *
 * `query` is optional and defaults to the first page ON PURPOSE: the menu badge
 * and the home read the same key with the same default, so the three share one
 * request instead of asking for a list each. They want `open` and nothing else —
 * the rows come along because they are already there.
 */
export function useMissions(query?: Ref<MissionQuery>, interval: number | false | null = null) {
	const page = query ?? ref(firstPage());
	return useQuery({
		queryKey: computed(() => missionKeys.list(page.value)),
		queryFn: async (): Promise<MissionListPage> =>
			request(`/v3/agents/missions?limit=${page.value.limit}&offset=${page.value.offset}`),
		refetchInterval: (q) => {
			if (interval !== null) return interval;
			return (q.state.data?.open ?? 0) > 0 ? 10_000 : false;
		},
		refetchOnWindowFocus: true,
		// O total no cabeçalho é a resposta a "tem mais?", e uma virada de página
		// que o apaga faz o número piscar entre duas verdades a cada clique.
		placeholderData: (previous) => previous,
	});
}

/**
 * One mission, by id.
 *
 * Its own query rather than a lookup in the list: the list is one PAGE, it carries no
 * steps at all, and a run on another page is still reachable — `GET
 * /v3/agents/missions/:id` answers whole. A panel open on a RUNNING mission also
 * wants a shorter leash than the list's ten seconds; the polling stops when the run
 * settles, so one left open is not a request every four seconds for the rest of the day.
 *
 * Segue perguntando com a aba em segundo plano, pela mesma razão que
 * `useOnboardingState`: uma missão roda por minutos, e minutos são exatamente o
 * tempo que alguém passa em outra aba. `refetchInterval` sozinho não dispara sem
 * foco, e o cliente global desliga `refetchOnWindowFocus` — as duas juntas fazem
 * uma missão que acabou continuar desenhada como "rodando" até alguém voltar.
 * `SETTLED` continua sendo quem fecha a torneira.
 */
export function useMission(id: Ref<string | null>, streaming?: Ref<boolean>) {
	return useQuery({
		queryKey: computed(() => missionKeys.one(id.value ?? "")),
		enabled: computed(() => Boolean(id.value)),
		queryFn: (): Promise<MissionRun> => request(`/v3/agents/missions/${id.value}`),
		// Com o fio ao vivo aberto (`useMissionLive`) quem chama é o evento, não o relógio.
		refetchInterval: (query) =>
			streaming?.value || (query.state.data && SETTLED.has(query.state.data.status))
				? false
				: 4_000,
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: true,
	});
}

/**
 * VÁRIAS corridas como UM percurso — os passos de todas, na ordem em que correram.
 *
 * Nasceu do primeiro dia (09/09/2026). Cada fase dele é um `mission_run` próprio,
 * e a tela da espera desenhava os passos da corrida CORRENTE: no instante em que
 * a pesquisa acabava e o perfil começava, as dezanove linhas que a pessoa tinha
 * visto acumular durante cinco minutos desapareciam todas, e a espera recomeçava
 * do zero à frente de quem estava a olhar.
 *
 * `useQueries` e não um pedido novo: cada corrida guarda a MESMA chave que
 * `useMission` usa (`missionKeys.one`), então uma que já foi aberta chega do
 * cache, e a trela de cada uma é a de sempre — as assentadas param de perguntar
 * sozinhas e só a viva continua a ser sondada de quatro em quatro segundos. Um
 * endpoint que devolvesse tudo junto voltaria a puxar as corridas terminadas
 * inteiras a cada volta, e uma missão de pesquisa pesa umas boas centenas de
 * kilobytes.
 */
export function useMissionTrail(ids: Ref<readonly string[]>, streaming?: Ref<boolean>) {
	return useQueries({
		queries: computed(() =>
			ids.value.map((id) => ({
				queryKey: missionKeys.one(id),
				queryFn: (): Promise<MissionRun> => request<MissionRun>(`/v3/agents/missions/${id}`),
				refetchInterval: (query: { state: { data?: MissionRun } }) =>
					streaming?.value || (query.state.data && SETTLED.has(query.state.data.status))
						? false
						: (4_000 as number | false),
				refetchIntervalInBackground: true,
				refetchOnWindowFocus: true,
			})),
		),
		combine: (results: ReadonlyArray<{ data?: MissionRun }>) => ({
			/**
			 * Os passos emendados, e nunca com um buraco no meio: uma corrida que
			 * ainda não chegou (ou que falhou a chegar) é SALTADA em vez de partir a
			 * lista. O percurso de uma tela de espera é melhor incompleto do que
			 * ausente.
			 */
			steps: results.flatMap((r) => r.data?.steps ?? []),
			runs: results.map((r) => r.data),
		}),
	});
}

/**
 * Stopping a mission.
 *
 * Both keys are invalidated because both are on screen: the row in the list
 * carries the badge, and the open panel carries the status somebody is watching.
 * Refreshing only one of them shows "rodando" beside "cancelada".
 *
 * A `running` mission does not stop mid-round — the turn in flight finishes on
 * its own — but it stays cancelled: the server refuses to write over a row that
 * was already stopped.
 */
export function useCancelMission() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			request<{ status: "cancelled"; roundInFlight: boolean; questionsDismissed: number }>(
				`/v3/agents/missions/${id}/cancel`,
				{ method: "POST" },
			),
		onSuccess: (_result, id) => {
			void client.invalidateQueries({ queryKey: missionKeys.one(id) });
			// O prefixo, e não uma página: `["missions"]` alcança toda página aberta
			// e a chave do detalhe junto — uma lista paginada tem uma chave por página.
			void client.invalidateQueries({ queryKey: ["missions"] });
		},
	});
}

export function useStartMission() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { agent?: string; objective: string; maxSteps?: number }) =>
			request<{ id: string; status: string; summary: string }>("/v3/agents/missions", {
				method: "POST",
				body: input,
				// A mission runs an agent loop: the default 15s would abort nearly
				// every one of them.
				timeoutMs: 300_000,
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["missions"] }),
	});
}
